// Thin API client for the admin panel. Mirrors eventshouse-web's pattern
// (token in localStorage, attached as a Bearer header, a global
// "admin:unauthorized" event on any 401 so any call site can trigger logout)
// but kept fully self-contained under src/admin/ rather than touching the
// site's existing Redux store.

const API_ENDPOINT = process.env.REACT_APP_NVFO_API_ENDPOINT || "http://localhost:4000/api/v1";
const TOKEN_KEY = "nvfo_admin_token";

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  details?: unknown;
  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

async function parseResponse(response: Response) {
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent("admin:unauthorized"));
    }
    throw new ApiError(response.status, body?.error || `request failed (${response.status})`, body?.details);
  }
  return body?.data;
}

export async function apiGet(path: string) {
  const token = getAuthToken();
  const response = await fetch(`${API_ENDPOINT}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return parseResponse(response);
}

export async function apiSend(
  method: "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
) {
  const token = getAuthToken();
  const response = await fetch(`${API_ENDPOINT}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

export async function apiUpload(path: string, formData: FormData) {
  const token = getAuthToken();
  const response = await fetch(`${API_ENDPOINT}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  });
  return parseResponse(response);
}
